import { useState, useEffect, useRef } from 'react';
import { Megaphone } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getAdBySlot } from '@/services/api';

const DANGEROUS = /enable_page_level_ads|google_ad_section|vignette|overlays|anchor/i;

function pushAdsense(ins) {
  if (!ins || ins.dataset.adPushed === '1' || typeof window === 'undefined') return;
  ins.dataset.adPushed = '1';
  const run = () => {
    try { (window.adsbygoogle = window.adsbygoogle || []).push({}); } catch (e) { /* already filled */ }
  };
  if (ins.offsetWidth > 0) run();
  else requestAnimationFrame(run);
}

function AdSenseUnit({ adsense, width, height, isHeader, className }) {
  const insRef = useRef(null);

  useEffect(() => {
    pushAdsense(insRef.current);
  }, []);

  const w = width || 300;
  const h = height || 250;

  return (
    <div
      data-testid="sponsored-ad-slot"
      className={cn('ad-slot relative mb-4 overflow-hidden', isHeader && 'mb-0 flex items-center justify-center', className)}
      style={{ width: '100%', maxWidth: w, height: h, minHeight: h }}
    >
      <ins
        ref={insRef}
        className="adsbygoogle"
        style={{ display: 'inline-block', width: w, height: h }}
        data-ad-client={adsense.client || ''}
        data-ad-slot={adsense.slot || ''}
        data-ad-format={adsense.responsive ? (adsense.format || 'auto') : undefined}
        data-full-width-responsive={adsense.responsive ? 'true' : undefined}
      />
    </div>
  );
}

function ScriptUnit({ html, width, height, isHeader, className }) {
  const wrapRef = useRef(null);
  const done = useRef(false);

  useEffect(() => {
    const container = wrapRef.current;
    if (done.current || !container) return;
    done.current = true;
    if (DANGEROUS.test(html || '')) {
      container.innerHTML = '';
      return;
    }
    const tmp = document.createElement('div');
    tmp.innerHTML = html || '';
    const insList = tmp.querySelectorAll('ins.adsbygoogle');
    if (insList.length) {
      Array.from(insList).forEach((ins) => {
        ins.removeAttribute('data-full-width-responsive');
        ins.style.maxWidth = '100%';
        ins.style.display = 'block';
        container.appendChild(ins);
      });
    } else {
      Array.from(tmp.childNodes).forEach((node) => {
        if (node.nodeName === 'SCRIPT') return;
        container.appendChild(node.cloneNode(true));
      });
    }
    container.querySelectorAll('ins.adsbygoogle').forEach(pushAdsense);
  }, [html]);

  return (
    <div
      data-testid="sponsored-ad-slot"
      className={cn('ad-slot relative mb-4 overflow-hidden', isHeader && 'mb-0', className)}
      style={width && height ? { width, minHeight: height, maxWidth: '100%' } : undefined}
    >
      <span className="mb-1.5 inline-block text-[0.65rem] uppercase tracking-wider text-slate-400">Sponsored</span>
      <div ref={wrapRef} />
    </div>
  );
}

/**
 * Matches MLS apps/ads/_slot.html + ads.css — same sponsored chrome.
 */
export const AdSlot = ({ slotId, size = 'box', className }) => {
  const [ad, setAd] = useState(undefined); // undefined=loading, null=hidden error

  useEffect(() => {
    let cancelled = false;
    getAdBySlot(slotId).then((result) => {
      if (!cancelled) setAd(result);
    });
    return () => { cancelled = true; };
  }, [slotId]);

  if (ad === undefined) return null;

  const { imageUrl, clickUrl, width, height, empty, applyUrl, adsense, scriptHtml } = ad || {};

  if (ad === null) {
    const isHeader = size === 'banner' || size === 'leaderboard';
    return (
      <a
        href="/advertise"
        data-testid="sponsored-ad-slot-empty"
        className={cn(
          'ad-slot ad-slot--empty group relative mx-auto flex w-full max-w-full flex-col items-center justify-center gap-1 rounded-md border border-dashed border-slate-300 px-4 py-3 text-center text-slate-400 no-underline transition-colors',
          'bg-[repeating-linear-gradient(45deg,#fafbfc,#fafbfc_10px,#f3f5f7_10px,#f3f5f7_20px)]',
          'hover:border-slate-400 hover:text-slate-600',
          isHeader && 'h-[58px] max-w-[470px] py-0',
          className,
        )}
      >
        <span className="absolute left-2 top-1.5 text-[0.65rem] uppercase tracking-wider text-slate-400">Ad</span>
        <Megaphone className={cn(isHeader ? 'h-3.5 w-3.5' : 'h-5 w-5')} strokeWidth={1.75} />
        <span className={cn('font-semibold', isHeader ? 'text-[0.7rem] text-slate-600' : 'text-sm')}>Advertise Here</span>
      </a>
    );
  }
  const aspectStyle = width && height ? { aspectRatio: `${width} / ${height}` } : undefined;
  const isHeader = size === 'banner' || size === 'leaderboard';

  if (adsense?.slot) {
    return (
      <AdSenseUnit
        adsense={adsense}
        width={width}
        height={height}
        isHeader={isHeader}
        className={className}
      />
    );
  }

  if (scriptHtml) {
    return (
      <ScriptUnit
        html={scriptHtml}
        width={width}
        height={height}
        isHeader={isHeader}
        className={className}
      />
    );
  }

  if (empty || !imageUrl) {
    return (
      <a
        href={applyUrl || '/advertise'}
        data-testid="sponsored-ad-slot-empty"
        className={cn(
          'ad-slot ad-slot--empty group relative mx-auto flex w-full max-w-full flex-col items-center justify-center gap-1 rounded-md border border-dashed border-slate-300 px-4 py-3 text-center text-slate-400 no-underline transition-colors',
          'bg-[repeating-linear-gradient(45deg,#fafbfc,#fafbfc_10px,#f3f5f7_10px,#f3f5f7_20px)]',
          'hover:border-slate-400 hover:text-slate-600',
          isHeader && 'h-[58px] max-w-[470px] py-0',
          className,
        )}
        style={aspectStyle}
      >
        <span className="absolute left-2 top-1.5 text-[0.65rem] uppercase tracking-wider text-slate-400">Ad</span>
        <Megaphone className={cn(isHeader ? 'h-3.5 w-3.5' : 'h-5 w-5')} strokeWidth={1.75} />
        <span className={cn('font-semibold', isHeader ? 'text-[0.7rem] text-slate-600' : 'text-sm')}>Advertise Here</span>
        {width && height && !isHeader && (
          <span className="text-[0.7rem] opacity-80">{width} x {height}</span>
        )}
      </a>
    );
  }

  return (
    <div
      data-testid="sponsored-ad-slot"
      className={cn('ad-slot relative mb-4 overflow-hidden', isHeader && 'mb-0 flex items-center justify-center', className)}
    >
      <span className="mb-1.5 inline-block text-[0.65rem] uppercase tracking-wider text-slate-400">Sponsored</span>
      <a href={clickUrl} target="_blank" rel="noopener noreferrer sponsored nofollow" className="block">
        <img
          src={imageUrl}
          alt="Sponsored"
          loading="lazy"
          width={width || undefined}
          height={height || undefined}
          className={cn(
            'block h-auto max-w-full rounded',
            isHeader && 'h-[58px] w-auto object-contain',
          )}
        />
      </a>
    </div>
  );
};
